import { useEffect, useState } from "react";
import { Flowmodoro, WeeklySchedule } from "../types/types";
import { addFlowmodoroRecord, getFlowmodoroRecordsPage } from "../api/flowmodoroRecords";
import { useIsLoggedIn } from "./useIsLoggedIn";
import { useSchedules } from "./useSchedules";
import {
  getPausedKey,
  isStaleSave,
  parseSavedState,
} from "../pages/Timer/timerPersistence";

export const pushOldFlowmodoros = async (
  weeklyFlowmodoros: WeeklySchedule[],
  now: number = Date.now()
) => {
  if (weeklyFlowmodoros.length !== 0) {
    for (let i = 0; i < weeklyFlowmodoros.length; i++) {
      const weeklyFlowmodoro = weeklyFlowmodoros[i];
      const sessionStorageCurrentSession = sessionStorage.getItem(
        weeklyFlowmodoro.name
      );
      if (sessionStorageCurrentSession) {
        const { totalSessionTime, currentTimeMillis } = JSON.parse(
          sessionStorageCurrentSession
        );
        // Remove before the request so a concurrent call (useRecords runs in
        // both FlowmodoroProvider and pages) can't submit the same minutes
        sessionStorage.removeItem(weeklyFlowmodoro.name);
        try {
          await addFlowmodoroRecord({
            date: currentTimeMillis,
            scheduleName: weeklyFlowmodoro.name,
            minutes: totalSessionTime,
            description: `Completed on ${new Date(currentTimeMillis).toString()}`,
          });
        } catch (error) {
          sessionStorage.setItem(weeklyFlowmodoro.name, sessionStorageCurrentSession);
          throw error;
        }
      }

      // A paused/interrupted session from a previous day won't be restored,
      // so submit its minutes instead of losing them. If the sessionStorage
      // entry above existed, it already covered this session (it's updated
      // every tick, so it's never behind the save) and submitting again would
      // double count.
      const pausedKey = getPausedKey(weeklyFlowmodoro.name);
      const rawSaved = localStorage.getItem(pausedKey);
      const saved = parseSavedState(rawSaved);
      if (saved && isStaleSave(saved, now)) {
        localStorage.removeItem(pausedKey);
        if (!sessionStorageCurrentSession && saved.totalSessionTime > 0) {
          try {
            await addFlowmodoroRecord({
              date: saved.savedAt,
              scheduleName: weeklyFlowmodoro.name,
              minutes: saved.totalSessionTime,
              description: `Completed on ${new Date(saved.savedAt).toString()}`,
            });
          } catch (error) {
            localStorage.setItem(pausedKey, rawSaved!);
            throw error;
          }
        }
      }
    }
  }
};

export const useRecords = () => {
  const [records, setRecords] = useState<Flowmodoro[]>([]);
  const {weeklyFlowmodoros, isLoadingWeeklyFlowmodoros} = useSchedules();
  const [isLoadingRecords, setIsLoadingRecords] = useState(true);
  const { isLoggedIn, isLoadingUser } = useIsLoggedIn();

  const updateRecords = async () => {
    if (!isLoadingUser && isLoggedIn) {
      await pushOldFlowmodoros(weeklyFlowmodoros);
      
      // Fetch first page
      const firstPage = await getFlowmodoroRecordsPage(200);
      setRecords(firstPage.records || []);
      setIsLoadingRecords(false);
      
      // Continue fetching remaining pages in the background
      if (firstPage.lastEvaluatedKey) {
        let lastKey: string | undefined = firstPage.lastEvaluatedKey;
        const allRecords = [...(firstPage.records || [])];
        
        while (lastKey) {
          const nextPage: Awaited<ReturnType<typeof getFlowmodoroRecordsPage>> = await getFlowmodoroRecordsPage(200, lastKey);
          if (nextPage.records) {
            allRecords.push(...nextPage.records);
            setRecords([...allRecords]); // Update UI with new records
          }
          lastKey = nextPage.lastEvaluatedKey;
        }
      }
    }

    if (!isLoadingUser && !isLoggedIn) {
      window.location.replace("/login");
    }
  };

  useEffect(() => {
    if(!isLoadingWeeklyFlowmodoros) {
      updateRecords();
    }
  }, [isLoadingRecords, isLoggedIn, isLoadingUser, isLoadingWeeklyFlowmodoros]);

  return {
    records,
    setRecords,
    isLoadingRecords,
    setIsLoadingRecords,
    updateRecords
  };
};
